import React, { useState, useEffect, useCallback } from 'react';
import { toZonedTime, fromZonedTime, format as formatTz, toDate } from 'date-fns-tz';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Stack,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete
} from '@mui/material';
import { 
  getOpportunity, 
  getPlaces, 
  addSailingOpportunity, 
  editOpportunity 
} from '../api/api';
const OpportunityEditor = ({ getToken, domainInfo }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [places, setPlaces] = useState([]);
  const [token, setToken] = useState(null);
  const [formData, setFormData] = useState({
    dateTime: '',
    date: '',
    time: '',
    place: '',
    type: 'cruising',
    name: '',
    minCertificationLevel: '',
    note: '',
    tz: domainInfo.tz
  });
  
  const [timeWarning, setTimeWarning] = useState(false);
  const [dateWarning, setDateWarning] = useState(false);

  useEffect(() => {
    const initToken = async () => {
      const newToken = await getToken();
      setToken(newToken);
    };
    initToken();
  }, [getToken]);

  const loadPlaces = useCallback(async () => {
    try {
      const data = await getPlaces(domainInfo.domain);
      setPlaces(data);
    } catch (error) {
      console.error('Error loading places:', error);
    }
  }, [domainInfo]);

  const loadOpportunity = useCallback(async () => {
    if (!isEditing || !token) return;
    
    try {
      setLoading(true);
      const opportunity = await getOpportunity(token, id);

      const timeZone = opportunity.tz ?? domainInfo.tz;
      console.log(timeZone);
      const utcDate = opportunity.dateTime;
      const zonedDate = toZonedTime(utcDate, timeZone);
      console.log(zonedDate);
      console.log("formatted zonedDate "  + formatTz(zonedDate, "HH:mm", { timeZone: timeZone }));

      setFormData({
        dateTime: opportunity.dateTime,
        date: formatTz(zonedDate, "yyyy-MM-dd", { timeZone: timeZone }),
        time: formatTz(zonedDate, "HH:mm", { timeZone: timeZone }),
        place: opportunity.place,
        type: opportunity.type,
        name: opportunity.name || '',
        minCertificationLevel: opportunity.minCertificationLevel || '',
        note: opportunity.note || '',
        tz: opportunity.tz ?? domainInfo.tz
      });
    } catch (error) {
      console.error('Error loading opportunity:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [token, id, isEditing, navigate]);

  useEffect(() => {
    loadPlaces();
  }, [loadPlaces]);

  useEffect(() => {
    if (token) {
      loadOpportunity();
    }
  }, [token, loadOpportunity]);

  // Check if time is within night hours (10 PM to 5 AM)
  const isNightTime = useCallback((timeStr) => {
    if (!timeStr) return false;
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours >= 22 || hours < 5);
  }, []);
  
  // Check if date is in the past
  const isPastDate = useCallback((dateStr) => {
    if (!dateStr) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of day for comparison
    
    const selectedDate = new Date(dateStr);
    selectedDate.setHours(0, 0, 0, 0);
    
    return selectedDate < today;
  }, []);
  
  // Update warnings when date or time changes
  useEffect(() => {
    setTimeWarning(isNightTime(formData.time));
    setDateWarning(isPastDate(formData.date));
  }, [formData.time, formData.date, isNightTime, isPastDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      };
      
      // If date or time changes, update the dateTime field
      if (name === 'date' || name === 'time') {
        if (newFormData.date && newFormData.time) {
          try {
            const dateTimeStr = `${newFormData.date}T${newFormData.time}`;
            const localDate = toDate(dateTimeStr, "yyyy-MM-dd'T'HH:mm", new Date());
            const utcDate = fromZonedTime(localDate, newFormData.tz);
            
            // Ensure we're working with a valid date before converting to ISO string
            if (isNaN(utcDate.getTime())) {
              throw new Error('Invalid date/time');
            }

            newFormData.dateTime = utcDate.toISOString();
          } catch (error) {
            console.error('Error parsing date/time:', error);
          }
        }
      }
      
      return newFormData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if date is in the past
    if (isPastDate(formData.date)) {
      setDateWarning(true);
      return;
    }
    
    try {
      if (isEditing) {
        await editOpportunity(token, id, formData);
      } else {
        await addSailingOpportunity(token, formData);
      }
      navigate('/');
    } catch (error) {
      console.error('Error saving opportunity:', error);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Paper
        sx={{
          width: '100%',
          maxWidth: 800,
          p: 3,
          border: '1px solid #ccc',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" component="h1" gutterBottom align="center">
          {isEditing ? t('opportunity.editTitle') : t('opportunity.createTitle')}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={1}>
          
            <TextField
              name="name"
              label={t('opportunity.form.name')}
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              margin="dense"
              slotProps={{ input: { maxLength: 100 } }}
            />

            <FormControl component="fieldset" margin="dense" required fullWidth>
             
              <RadioGroup
                aria-label="type"
                name="type"
                value={formData.type ?? 'racing'}
                onChange={handleChange}
                row
              >
                <FormControlLabel 
                  value="cruising" 
                  control={<Radio />} 
                  label={t('opportunity.form.cruising')} 
                />
                <FormControlLabel 
                  value="racing" 
                  control={<Radio />} 
                  label={t('opportunity.form.racing')} 
                />
          
              </RadioGroup>
            </FormControl> 
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ width: '100%' }}>
                <TextField
                  name="date"
                  label={t('opportunity.form.date')}
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  error={dateWarning}
                  helperText={dateWarning ? "Date cannot be in the past" : ""}
                  sx={{
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      filter: dateWarning ? 'grayscale(100%)' : 'none'
                    }
                  }}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <TextField
                  name="time"
                  label={t('opportunity.form.time')}
                  type="time"
                  value={formData.time}
                  onChange={handleChange}
                  fullWidth
                  required
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  error={timeWarning}
                  helperText={timeWarning ? "Night hours (10 PM - 5 AM) are outside of the search interval. Сrew won't be notified." : ""}
                  sx={{
                    '& input[type="time"]::-webkit-calendar-picker-indicator': {
                      filter: timeWarning ? 'grayscale(100%)' : 'none'
                    }
                  }}
                />
              </Box>
            </Box>

            <Autocomplete
              fullWidth
              options={places}
              getOptionLabel={(option) => option.name}
              value={places.find(place => place._id === formData.place) || null}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  place: newValue ? newValue._id : ''
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('opportunity.form.location')}
                  margin="normal"
                  required
                />
              )}
            />

            <TextField
              name="note"
              label={t('opportunity.form.description')}
              value={formData.note}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              margin="normal"
              slotProps={{ input: { maxLength: 1000 } }}
            />

           

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={handleCancel}>
                {t('opportunity.form.cancel')}
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {isEditing ? t('opportunity.form.save') : t('opportunity.form.create')}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default OpportunityEditor;
